import {CollectionViewer, DataSource, SelectionChange} from '@angular/cdk/collections';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';
import {FlatTreeControl} from '@angular/cdk/tree';
import {CacheDataService} from '../../../api/services/cache-data.service';
import {merge} from 'rxjs';
import {Observable} from 'rxjs/internal/Observable';
import {map} from 'rxjs/operators';
import {TreeViewSelectionNode} from './tree-view-selection-node';

export abstract class TreeViewDataSource<TSourceType, TSourceKeyType, TNodeType extends TreeViewSelectionNode<TSourceType, TSourceKeyType>> implements DataSource<TNodeType> {
  dataChange = new BehaviorSubject<TNodeType[]>([]);

  get data(): TNodeType[] {
    return this.dataChange.value;
  }
  set data(value: TNodeType[]) {
    this._treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  public constructor(
    private _treeControl: FlatTreeControl<TNodeType>) {
  }

  protected abstract loadInitialData(callback: (results: TNodeType[]) => void);
  protected abstract loadChildNodes(node: TNodeType, callback: (results: TNodeType[]) => void);

  connect(collectionViewer: CollectionViewer): Observable<TNodeType[]> {
    this._treeControl.expansionModel.changed.subscribe(change => {
      if (
        (change as SelectionChange<TNodeType>).added ||
        (change as SelectionChange<TNodeType>).removed
      ) {
        this.handleTreeControl(change as SelectionChange<TNodeType>);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  disconnect(collectionViewer: CollectionViewer): void {}

  refresh() {
    this.dataChange.next(this.data);
  }

  handleTreeControl(change: SelectionChange<TNodeType>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed && change.removed.length > 0) {
      change.removed
        .slice()
        .reverse()
        .forEach(node => this.toggleNode(node, false));

    }
  }

  public loadRootItems() {
    this.loadInitialData(results => {
      this.data = results;
      this.emitChanges(undefined);
    });
  }

  public blankCover(src: string | undefined | null) : string {
    if (src === undefined || src === null) {
      return "(Blank)";
    } else {
      if (src.trim() === '') {
        return "(Blank)";
      }

      return src.trim();
    }
  }

  toggleNode(node: TNodeType, expand: boolean) {

    if (!expand) {
      node.children = [];
      let toRemove : TNodeType[] = [];
      for (let i = 0; i < this.data.length; i++) {
        if (this.data[i] != node && this.data[i].isChildOf(node.sourceKey)) {
          toRemove.push(this.data[i]);
        }
      }

      toRemove.forEach(x => this.data.splice(this.data.indexOf(x), 1));
      this.emitChanges(undefined);
    } else {
      const index = this.data.indexOf(node);
      node.isLoading = true;

      this.loadChildNodes(node, results => {
        if (results.length == 0) {
          node.expandable = false;
        } else {
          node.children = results;
          this.data.splice(index + 1, 0, ...results);
        }
        this.emitChanges(node);
      });
    }
  }

  private emitChanges(node: TNodeType | undefined) {
    this.dataChange.next(this.data);
    if(node) node.isLoading = false;
  }

}
